import * as React from 'react';
import MyTypes from 'MyTypes';
import { connect, ConnectedProps } from 'react-redux';
import { bindActionCreators, Dispatch } from 'redux';
import { 
    Row, Col, Card, CardBody, CardHeader, Collapse, Label, Input, Badge, Nav, NavItem, NavLink, TabContent, TabPane, Container,
    UncontrolledDropdown, DropdownToggle, DropdownMenu, DropdownItem, Button, Spinner
} from 'reactstrap';
import { 
    FaPlay, FaSave, FaPlus, FaDatabase, FaFileAlt, FaRobot, FaCode, FaInfoCircle,
    FaEye, FaRocket, FaCloudUploadAlt, FaQuestionCircle, FaTags, FaTrash, FaUndo, FaArrowLeft, FaLayerGroup, FaMicrochip, FaGlobe, FaArrowsAltV, FaChevronDown, FaChevronUp, FaCheckSquare, FaSquare, FaListUl
} from 'react-icons/fa';
import * as router from '../../../services/router-service';
import ConfirmModal from 'components/app/ConfirmModal';
import { isAiDocumentFile } from '../aiDocumentFileTypes';
import { request, ApiResponse } from 'services/api/base';
import { companyActions } from 'store/companies';
import { aiLabActions } from 'store/aiLab';
import * as models from 'models';
import services from 'services';
import AIDesignerGuideModal from './AIDesignerGuideModal';
import AIPublishModal from './AIPublishModal';

export interface DocumentSectionMeta {
    key: string;
    title: string;
    blobPath: string;
}

export interface ToolSlot {
    id: string;
    type: 'dynamic' | 'static' | 'file'; 
    title: string;
    label: string; 
    content?: string; 
    companyFileTypeId?: number; 
    meta?: string;
}

export interface GlobalInputSlot {
    id: string;
    type: 'dynamic' | 'file';
    title: string;
    alias: string; 
    companyFileTypeId?: number; 
    content?: string; 
    meta?: string;
}

export interface PipelineStep {
    id: string;
    stepNumber: number;
    title: string;
    selectedModel: string;
    isOptionalPhase: boolean;
    selectedSectionKeys?: string[];
    slots: ToolSlot[];
}

interface CombinedProps extends PropsFromRedux {
    match?: {
        params: {
            toolId?: string;
        }
    };
}

const AVAILABLE_MODELS = [
    { id: 'gpt-4o', name: 'Azure OpenAI - GPT-4o' },
    { id: 'gpt-5.4', name: 'Azure OpenAI - GPT-5.4' }
];

const getInitialState = () => ({
    activePreviewTab: 'preview',
    showPublishModal: false,
    showGuideModal: false,
    showResetModal: false,
    copiedTab: null as string | null,
    
    // UI Layout States
    draggingStepId: null as string | null,
    collapsedSteps: {} as Record<string, boolean>, 
    
    // Publish / Configuration Data
    configurationId: null as number | null,
    isClaimLevel: false,
    linkedCarrierId: null as number | null,
    publishComments: "",
    toolName: "New Pipeline Bot", 
    description: "",
    uiTemplate: "", 
    versionContentBaseline: undefined as string | undefined,
    
    // Testing & Polling Data
    isTesting: false,
    sandboxExecutionJobId: null as number | string | null,
    testOutputHtml: "",
    testOutputRawJson: "", 
    testOutputLedgerJson: "",
    selectedMockClaimId: "", 
    
    // Global Context Layer
    globalPersona: 'You are an automated pipeline processing entity. Your role is to fulfill system transformations natively.',
    globalInputs: [
        {
            id: 'global-f-1',
            type: 'file',
            title: 'Global Reference Bundle',
            alias: '#GlobalReference',
            companyFileTypeId: 0,
            content: '',
            meta: 'Staged'
        }
    ] as GlobalInputSlot[],

    // Sequential Graph Steps
    steps: [
        {
            id: 'step-1',
            stepNumber: 1,
            title: 'Phase 1',
            selectedModel: 'gpt-4o',
            isOptionalPhase: false,
            selectedSectionKeys: [] as string[],
            slots: [] as ToolSlot[]
        }
    ] as PipelineStep[]
});

class AIDesignerContainer extends React.Component<CombinedProps, any> {
    public state = getInitialState();
    private sandboxPollTimer: NodeJS.Timeout | null = null;

    public componentDidMount() {
        this.props.requestCompanyFileTypesDD();
        this.props.requestInsCompanyDD();
        
        const routeToolId = this.props.match?.params?.toolId;
        if (routeToolId) {
            const parsedId = parseInt(routeToolId, 10);
            if (!isNaN(parsedId) && parsedId > 0) {
                this.props.requestAiToolConfiguration(parsedId);
            }
        }
    }

    public componentWillUnmount() {
        this.clearSandboxPolling();
    }

    private clearSandboxPolling = () => {
        if (this.sandboxPollTimer) {
            clearInterval(this.sandboxPollTimer);
            this.sandboxPollTimer = null;
        }
    };

    private executePublishPipeline = (targetId: number) => {
        const { toolName, description, isClaimLevel, publishComments } = this.state;

        const publishModel = {
            id: targetId,
            name: toolName,
            description: description,
            isClaimLevel: isClaimLevel,
            comments: publishComments || "Deployment Release Milestone"
        };
        this.props.publishAiToolVersion(publishModel);
    }

    public componentDidUpdate(prevProps: CombinedProps) {
        const routeToolId = this.props.match?.params?.toolId;
        const prevRouteToolId = prevProps.match?.params?.toolId;

        if (routeToolId && routeToolId !== prevRouteToolId) {
            const parsedId = parseInt(routeToolId, 10);
            if (!isNaN(parsedId)) {
                this.props.requestAiToolConfiguration(parsedId);
            }
        }

        if (prevRouteToolId && !routeToolId) {
            this.clearSandboxPolling();
            this.setState(getInitialState());
            return;
        }

        if (this.props.aiToolConfiguration && this.props.aiToolConfiguration !== prevProps.aiToolConfiguration) {
            const tool = this.props.aiToolConfiguration;
            const isBrandNewLoad = this.state.configurationId === null || this.state.configurationId !== tool.id;

            if (isBrandNewLoad) {
                const activeVersion = tool.versions?.find((v: any) => !v.isPublished) || 
                                      tool.latestPublishedVersion || 
                                      tool.currentVersion || 
                                      {} as models.AiToolVersion;
                
                let parsedSteps = getInitialState().steps;
                let loadedPersona = getInitialState().globalPersona;
                let loadedGlobalInputs = getInitialState().globalInputs;
                const initialCollapses: Record<string, boolean> = {};

                if (activeVersion.pipelineJson) {
                    try {
                        const parsedData = JSON.parse(activeVersion.pipelineJson);
                        
                        if (parsedData.steps) {
                            // Defaults applied here must also be applied in AiToolVersionContent.NormalizePipeline (Core).
                            parsedSteps = parsedData.steps.map((s: any, idx: number) => {
                                initialCollapses[s.id] = true; 
                                return {
                                    ...s,
                                    stepNumber: idx + 1,
                                    isOptionalPhase: s.isOptionalPhase ?? false,
                                    selectedSectionKeys: s.selectedSectionKeys || [],
                                    slots: s.slots || []
                                };
                            });
                            loadedPersona = parsedData.globalPersona || loadedPersona;
                            loadedGlobalInputs = parsedData.globalInputs || parsedData.globalFiles || [];
                        }
                    } catch (e) {
                        console.error("Failed compiling pipeline configurations.", e);
                    }
                } else {
                    initialCollapses['step-1'] = true;
                }

                this.setState({
                    configurationId: tool.id,
                    toolName: tool.name || "New Pipeline Bot",
                    description: tool.description || "",
                    isClaimLevel: tool.isClaimLevel,
                    linkedCarrierId: tool.linkedCarrierId ?? null,
                    uiTemplate: activeVersion.uiTemplate || "",
                    globalPersona: loadedPersona,
                    globalInputs: loadedGlobalInputs,
                    steps: parsedSteps,
                    collapsedSteps: initialCollapses,
                    testOutputHtml: "",
                    testOutputRawJson: "",
                    testOutputLedgerJson: ""
                }, () => {
                    this.setState({ versionContentBaseline: this.getVersionContentSignature() });
                    this.loadAllReferencedSections();
                });
            }
        }

        if (this.state.configurationId === null && this.props.aiToolConfiguration && !prevProps.aiToolConfiguration) {
            const freshTool = this.props.aiToolConfiguration;
            this.setState({ configurationId: freshTool.id }, () => {
                router.goto(`/ai-lab/designer/${freshTool.id}`);
                if (this.state.showPublishModal) {
                    this.executePublishPipeline(freshTool.id);
                }
            });
        }

        if (prevProps.isToolPublishing && !this.props.isToolPublishing) {
            if (!this.props.publishError) {
                this.setState({ showPublishModal: false }, () => {
                    router.goto(services.router.url.aiLabCatalog); 
                });
            } 
        }

        if (prevProps.isToolUpdating && !this.props.isToolUpdating) {
            if (!this.props.updateError) {
                this.setState({ versionContentBaseline: this.getVersionContentSignature() });
                if (this.state.publishComments === "PENDING_DEPLOYMENT_RECOVERY_TOKEN") {
                    this.setState({ 
                        showPublishModal: true, 
                        publishComments: "Deploying updated optimization changes." 
                    });
                }
            }
        }
    }

    private loadAllReferencedSections = () => {
        const { linkedCarrierId } = this.state;
        const fileTypeIds = new Set<number>();
        this.state.globalInputs.forEach((gi: GlobalInputSlot) => {
            if (gi.companyFileTypeId && gi.companyFileTypeId > 0) fileTypeIds.add(gi.companyFileTypeId);
        });

        fileTypeIds.forEach(id => {
            this.props.requestFileTypeSections(id, linkedCarrierId);
        });
    }

    private handleLinkedCarrierChange = (rawValue: string) => {
        const linkedCarrierId = rawValue ? parseInt(rawValue, 10) : null;
        const normalizedId = linkedCarrierId && linkedCarrierId > 0 ? linkedCarrierId : null;

        const clearedSteps = this.state.steps.map((step: PipelineStep) => ({
            ...step,
            selectedSectionKeys: [] as string[]
        }));

        this.setState({ linkedCarrierId: normalizedId, steps: clearedSteps }, () => {
            this.loadAllReferencedSections();
        });
    }

    private toggleResetModal = () => this.setState({ showResetModal: !this.state.showResetModal });

    private handleResetConfirm = () => {
        this.clearSandboxPolling();
        this.setState(getInitialState(), () => {            
            router.goto('/ai-lab/designer');
        });
    }

    private togglePublishModal = () => {
        const { configurationId } = this.state;
        const tool = this.props.aiToolConfiguration;

        if (!this.state.showPublishModal && tool) {
            this.setState({ 
                showPublishModal: true, 
                toolName: tool.name || "", 
                description: tool.description || "", 
                isClaimLevel: tool.isClaimLevel !== undefined ? tool.isClaimLevel : true,
                publishComments: configurationId ? "Deploying updated optimization changes." : ""
            });
        } else {
            this.setState({ 
                showPublishModal: !this.state.showPublishModal 
            });
        }
    }

    private toggleBulkPhaseDisplay = (shouldCollapse: boolean) => {
        const updatedCollapses: Record<string, boolean> = {};
        this.state.steps.forEach((s: PipelineStep) => {
            updatedCollapses[s.id] = shouldCollapse;
        });
        this.setState({ collapsedSteps: updatedCollapses });
    }

    private handlePublishButtonClick = () => {
        this.setState({ publishComments: "PENDING_DEPLOYMENT_RECOVERY_TOKEN" }, () => { 
            const payload = this.compilePayload();          
            if (this.state.configurationId) {
                this.props.updateAiToolConfiguration(this.state.configurationId, payload as models.AiToolConfiguration);
            } else {
                this.props.createAiToolConfiguration(payload as models.CreateAiToolConfiguration); 
            }
        });
    }

    private compilePayload = () => {
        const { configurationId, toolName, description, isClaimLevel, linkedCarrierId, steps, uiTemplate, globalPersona, globalInputs } = this.state;
        const attachments: any[] = [];
        let requiresRuntimeUpload = false;

        const cleanGlobalInputs = globalInputs.map((gi: GlobalInputSlot) => {
            if (gi.type === 'file' && (!gi.companyFileTypeId || gi.companyFileTypeId === 0)) {
                if (gi.content && gi.content.trim() !== '') {
                    const fileNames = gi.content.split(',');
                    fileNames.forEach(fn => {
                        if (fn.trim()) {
                            attachments.push({
                                slotIdentifier: gi.id,
                                blobFileName: fn.trim(),
                                displayName: gi.alias || 'Global Reference Context'
                            });
                        }
                    });
                } else {
                    requiresRuntimeUpload = true;
                }
                return { ...gi, content: '' };
            }
            return gi;
        });

        const cleanSteps = steps.map((step: PipelineStep) => {
            const cleanSlots = step.slots.map((slot: ToolSlot) => {
                if (slot.type === 'file' && (!slot.companyFileTypeId || slot.companyFileTypeId === 0)) {
                    if (slot.content && slot.content.trim() !== '') {
                        const fileNames = slot.content.split(',');
                        fileNames.forEach(fn => {
                            if (fn.trim()) {
                                attachments.push({
                                    slotIdentifier: slot.id,
                                    blobFileName: fn.trim(),
                                    displayName: slot.label || 'Attached Context'
                                });
                            }
                        });
                    } else {
                        if (!step.isOptionalPhase) {
                            requiresRuntimeUpload = true;
                        }
                    }
                    return { ...slot, content: '' }; 
                }
                return slot;
            });
            return { ...step, slots: cleanSlots };
        });

        const unifiedEnvelopeJson = JSON.stringify({
            globalPersona: globalPersona,
            globalInputs: cleanGlobalInputs,
            steps: cleanSteps
        });

        if (configurationId) {
            return {
                id: configurationId,
                name: toolName,
                description: description,
                isClaimLevel: isClaimLevel,
                linkedCarrierId: linkedCarrierId,
                isActive: true,
                currentVersion: {
                    pipelineJson: unifiedEnvelopeJson,
                    uiTemplate: uiTemplate,
                    requiresRuntimeUpload: requiresRuntimeUpload,
                    attachments: attachments
                }
            } as models.AiToolConfiguration;
        }

        return {
            name: toolName,
            description: description,
            isClaimLevel: isClaimLevel,
            linkedCarrierId: linkedCarrierId,
            pipelineJson: unifiedEnvelopeJson,
            uiTemplate: uiTemplate,
            requiresRuntimeUpload: requiresRuntimeUpload,
            attachments: attachments
        } as models.CreateAiToolConfiguration;
    }

    private getVersionContentSignature = (): string => {
        const payload: any = this.compilePayload();
        const version = payload.currentVersion ?? payload;
        return JSON.stringify({
            pipelineJson: version.pipelineJson,
            uiTemplate: version.uiTemplate,
            requiresRuntimeUpload: version.requiresRuntimeUpload,
            attachments: version.attachments
        });
    }

    private handleSaveDraft = () => {
        this.setState({ publishComments: "" }); 
        const payload = this.compilePayload();          
        if (this.state.configurationId) {
            this.props.updateAiToolConfiguration(this.state.configurationId, payload as models.AiToolConfiguration);
        } else {
            this.props.createAiToolConfiguration(payload as models.CreateAiToolConfiguration); 
        }
    }

    private handlePublish = () => {
        const { configurationId } = this.state;
        const payload = this.compilePayload();

        if (configurationId) {
            this.executePublishPipeline(configurationId);
        } else {
            this.props.createAiToolConfiguration(payload as models.CreateAiToolConfiguration);
        }
    }

    private pollSandboxExecutionStatus = (jobId: number | string) => {
        this.clearSandboxPolling();

        this.sandboxPollTimer = setInterval(async () => {
            try {
                const res: ApiResponse = await request({
                    method: 'GET',
                    url: `/ailab/sandbox/executions/${jobId}/status`
                });

                if (res.isSuccess && res.data) {
                    const data = res.data;

                    if (data.status === 'Complete') {
                        this.clearSandboxPolling();

                        let rawText = data.rawResponse || "";
                        let cleanJsonText = "";
                        let cleanLedgerText = "";
                        
                        const jsonMatch = rawText.match(/\{[\s\S]*\}/);
                        if (jsonMatch) {
                            try {
                                const parsedObj = JSON.parse(jsonMatch[0]);
                                cleanJsonText = JSON.stringify(parsedObj, null, 2);
                            } catch (e) {
                                cleanJsonText = jsonMatch[0];
                            }
                        } else {
                            cleanJsonText = rawText || "// No valid JSON footprint detected in backend payload.";
                        }

                        if (data.accumulatedLedger || data.ledger) {
                            try {
                                const ledgerSourceObj = data.accumulatedLedger || data.ledger;
                                cleanLedgerText = typeof ledgerSourceObj === 'string' ? ledgerSourceObj : JSON.stringify(ledgerSourceObj, null, 2);
                            } catch (err) {
                                cleanLedgerText = "// Failed formatting transient state ledger matrix blocks.";
                            }
                        } else if (data.pipelineLedger) {
                            cleanLedgerText = data.pipelineLedger;
                        } else {
                            cleanLedgerText = rawText ? "// Ledger state trace merged inside response structure. Check Raw JSON payload." : "// No system ledger matrix constructed.";
                        }

                        this.setState({
                            isTesting: false,
                            sandboxExecutionJobId: null,
                            testOutputHtml: data.formattedHtml || data.finalHtml || data.outputHtml,
                            testOutputRawJson: cleanJsonText,
                            testOutputLedgerJson: cleanLedgerText
                        });

                    } else if (data.status === 'Failed') {
                        this.clearSandboxPolling();
                        const errorMsg = data.errorMessage || 'Execution failed on background sandbox processor.';
                        this.setState({
                            isTesting: false,
                            sandboxExecutionJobId: null,
                            testOutputHtml: `
                                <div class="alert alert-danger shadow-sm border-0">
                                    <h6 class="fw-bold"><i class="fa fa-exclamation-triangle me-2"></i>AI Execution Error</h6>
                                    <p class="small mb-0">${errorMsg}</p>
                                </div>`,
                            testOutputRawJson: JSON.stringify({ error: errorMsg }, null, 2),
                            testOutputLedgerJson: JSON.stringify({ error: errorMsg }, null, 2)
                        });
                    }
                }
            } catch (err: any) {
                this.clearSandboxPolling();
                const errorMsg = err.message || 'Error communicating with sandbox execution polling endpoint.';
                this.setState({
                    isTesting: false,
                    sandboxExecutionJobId: null,
                    testOutputHtml: `
                        <div class="alert alert-danger shadow-sm border-0">
                            <h6 class="fw-bold"><i class="fa fa-exclamation-triangle me-2"></i>Polling Communication Error</h6>
                            <p class="small mb-0">${errorMsg}</p>
                        </div>`,
                    testOutputRawJson: JSON.stringify({ error: errorMsg }, null, 2),
                    testOutputLedgerJson: JSON.stringify({ error: errorMsg }, null, 2)
                });
            }
        }, 3000);
    };

    private handleRunTest = async () => {
        const { steps, uiTemplate, selectedMockClaimId, globalPersona, globalInputs, linkedCarrierId } = this.state;
        
        let hasDynamicContext = globalInputs.some((gi: GlobalInputSlot) => gi.type === 'dynamic');
        if (!hasDynamicContext) {
            steps.forEach((step: PipelineStep) => {
                if (step.slots.some((s: ToolSlot) => s.type === 'dynamic')) {
                    hasDynamicContext = true;
                }
            });
        }

        const hasCarrierFileBinding = globalInputs.some((gi: GlobalInputSlot) =>
            gi.type === 'file' && !!gi.companyFileTypeId && gi.companyFileTypeId > 0
        );

        if (hasDynamicContext && (!selectedMockClaimId || selectedMockClaimId.trim() === "")) {
            alert("This configuration utilizes dynamic Runtime Data Object Injection. Please supply an integration tracking lookup context parameter to run the simulation.");
            return;
        }

        if (hasCarrierFileBinding && !linkedCarrierId && (!selectedMockClaimId || selectedMockClaimId.trim() === "")) {
            alert("This tool binds carrier catalog files. Link a carrier on the tool, or supply a mock claim so sandbox can resolve the carrier files.");
            return;
        }

        this.clearSandboxPolling();
        this.setState({ 
            isTesting: true, 
            sandboxExecutionJobId: null,
            testOutputHtml: "", 
            testOutputRawJson: "", 
            testOutputLedgerJson: "" 
        });

        const payload = {
            pipelineJson: JSON.stringify({
                globalPersona: globalPersona,
                globalInputs: globalInputs,
                steps: steps
            }),
            uiTemplate: uiTemplate,
            mockClaimId: selectedMockClaimId ? selectedMockClaimId.trim() : "",
            linkedCarrierId: linkedCarrierId || null
        };

        const response: ApiResponse = await request({
            method: 'POST',
            url: '/ailab/sandbox',
            data: payload
        });

        if (response.isSuccess && response.data) {
            const jobId = response.data.executionLogId || response.data.jobId;
            if (jobId) {
                this.setState({ sandboxExecutionJobId: jobId });
                this.pollSandboxExecutionStatus(jobId);
            } else if (response.data.formattedHtml || response.data.finalHtml || response.data.rawResponse) {
                // Fallback direct synchronous execution support
                let rawText = response.data.rawResponse || "";
                let cleanJsonText = "";
                let cleanLedgerText = "";
                
                const jsonMatch = rawText.match(/\{[\s\S]*\}/);
                if (jsonMatch) {
                    try {
                        const parsedObj = JSON.parse(jsonMatch[0]);
                        cleanJsonText = JSON.stringify(parsedObj, null, 2);
                    } catch (e) {
                        cleanJsonText = jsonMatch[0];
                    }
                } else {
                    cleanJsonText = rawText || "// No valid JSON footprint detected in backend payload.";
                }

                if (response.data.accumulatedLedger || response.data.ledger) {
                    try {
                        const ledgerSourceObj = response.data.accumulatedLedger || response.data.ledger;
                        cleanLedgerText = typeof ledgerSourceObj === 'string' ? ledgerSourceObj : JSON.stringify(ledgerSourceObj, null, 2);
                    } catch (err) {
                        cleanLedgerText = "// Failed formatting transient state ledger matrix blocks.";
                    }
                } else if (response.data.pipelineLedger) {
                    cleanLedgerText = response.data.pipelineLedger;
                } else {
                    cleanLedgerText = rawText ? "// Ledger state trace merged inside response structure. Check Raw JSON payload." : "// No system ledger matrix constructed.";
                }

                this.setState({ 
                    isTesting: false, 
                    testOutputHtml: response.data.formattedHtml || response.data.finalHtml,
                    testOutputRawJson: cleanJsonText,
                    testOutputLedgerJson: cleanLedgerText
                });
            }
        } else {
            this.setState({ 
                isTesting: false, 
                testOutputHtml: `
                    <div class="alert alert-danger shadow-sm border-0">
                        <h6 class="fw-bold"><i class="fa fa-exclamation-triangle me-2"></i>AI Execution Error</h6>
                        <p class="small mb-0">${response.getError()}</p>
                    </div>`,
                testOutputRawJson: JSON.stringify({ error: response.getError() }, null, 2),
                testOutputLedgerJson: JSON.stringify({ error: response.getError() }, null, 2)
            });
        }
    }

    private toggleGuideModal = () => this.setState({ showGuideModal: !this.state.showGuideModal });

    private togglePreviewTab = (tab: string) => {
        if (this.state.activePreviewTab !== tab) {
            this.setState({ activePreviewTab: tab });
        }
    }

    private toggleStepCollapse = (stepId: string) => {
        const current = { ...this.state.collapsedSteps };
        current[stepId] = !current[stepId];
        this.setState({ collapsedSteps: current });
    }

    private addGlobalInputSlot = (type: GlobalInputSlot['type']) => {
        const { globalInputs } = this.state;
        const titles: any = {
            'dynamic': 'Object Blueprint Injection',
            'file': 'Global Reference Bundle'
        };

        const newSlot: GlobalInputSlot = {
            id: 'global-s-' + (globalInputs.length + 1).toString() + "-" + Math.random().toString(36).substr(2, 4),
            type: type,
            title: titles[type],
            alias: type === 'dynamic' ? '{{Global_JSON}}' : `#GlobalReference${globalInputs.length + 1}`,
            companyFileTypeId: 0,
            content: type === 'dynamic' ? 'Runtime Injection Core' : '',
            meta: type === 'dynamic' ? 'Unbound Data Matrix' : 'Ready'
        };
        this.setState({ globalInputs: [...globalInputs, newSlot] });
    }

    private removeGlobalInputSlot = (id: string) => {
        this.setState({ globalInputs: this.state.globalInputs.filter((x: GlobalInputSlot) => x.id !== id) });
    }

    private updateGlobalInputSlotProperty = (id: string, property: keyof GlobalInputSlot, value: any) => {
        const updated = this.state.globalInputs.map((x: GlobalInputSlot) => {
            if (x.id === id) {
                return { ...x, [property]: value };
            }
            return x;
        });
        this.setState({ globalInputs: updated }, () => {
            if (property === 'companyFileTypeId' && value > 0) {
                this.props.requestFileTypeSections(value, this.state.linkedCarrierId);
            }
        });
    }

    private addNewStepGroup = () => {
        const { steps, collapsedSteps } = this.state;
        const newId = 'step-' + (steps.length + 1).toString() + "-" + Math.random().toString(36).substr(2, 4);
        
        const newStep: PipelineStep = {
            id: newId,
            stepNumber: steps.length + 1,
            title: `Execution Phase ${steps.length + 1}`,
            selectedModel: 'gpt-4o',
            isOptionalPhase: false,
            selectedSectionKeys: [],
            slots: []
        };
        
        this.setState({ 
            steps: [...steps, newStep],
            collapsedSteps: { ...collapsedSteps, [newId]: false } 
        });
    }

    private removeStepGroup = (stepId: string) => {
        const filtered = this.state.steps.filter((s: PipelineStep) => s.id !== stepId);
        const reindexed = filtered.map((s: PipelineStep, idx: number) => ({
            ...s,
            stepNumber: idx + 1
        }));
        this.setState({ steps: reindexed });
    }

    private updateStepProperty = (stepId: string, property: keyof PipelineStep, value: any) => {
        const updated = this.state.steps.map((s: PipelineStep) => {
            if (s.id === stepId) {
                return { ...s, [property]: value };
            }
            return s;
        });
        this.setState({ steps: updated });
    }

    private toggleStepSectionKey = (stepId: string, sectionKey: string) => {
        const updatedSteps = this.state.steps.map((step: PipelineStep) => {
            if (step.id === stepId) {
                const currentKeys = step.selectedSectionKeys || [];
                const exists = currentKeys.includes(sectionKey);
                const nextKeys = exists 
                    ? currentKeys.filter(k => k !== sectionKey)
                    : [...currentKeys, sectionKey];
                return { ...step, selectedSectionKeys: nextKeys };
            }
            return step;
        });
        this.setState({ steps: updatedSteps });
    }

    private toggleAllStepSections = (stepId: string, allKeys: string[], selectAll: boolean) => {
        const updatedSteps = this.state.steps.map((step: PipelineStep) => {
            if (step.id === stepId) {
                return { ...step, selectedSectionKeys: selectAll ? [...allKeys] : [] };
            }
            return step;
        });
        this.setState({ steps: updatedSteps });
    }

    private addSlotToStep = (stepId: string, type: ToolSlot['type']) => {
        const { steps } = this.state;
        const titles: any = {
            'dynamic': 'Claim Object Injection',
            'file': 'Generic File Bundle', 
            'static': 'Static Instruction'
        };

        const updated = steps.map((step: PipelineStep) => {
            if (step.id === stepId) {
                const newSlot: ToolSlot = {
                    id: (step.slots.length + 1).toString() + "-" + Math.random().toString(36).substr(2, 5), 
                    type: type,
                    title: titles[type] || 'New Context Slot',
                    label: type === 'file' ? 'Attachment Bundle' : '', 
                    content: type === 'dynamic' ? '{{Claim_JSON}}' : '',
                    meta: type === 'dynamic' ? 'Injected at Runtime' : ''
                };
                return { ...step, slots: [...step.slots, newSlot] };
            }
            return step;
        });
        this.setState({ steps: updated });
    }

    private removeSlotFromStep = (stepId: string, slotId: string) => {
        const updated = this.state.steps.map((step: PipelineStep) => {
            if (step.id === stepId) {
                return { ...step, slots: step.slots.filter(s => s.id !== slotId) };
            }
            return step;
        });
        this.setState({ steps: updated });
    }

    private handleDragOver = (e: React.DragEvent) => {
        e.preventDefault(); 
        e.stopPropagation();
    }

    private handlePhaseDragStart = (e: React.DragEvent, stepId: string) => {
        const currentCollapses = { ...this.state.collapsedSteps };
        this.state.steps.forEach((s: PipelineStep) => {
            currentCollapses[s.id] = true;
        });
        this.setState({ 
            draggingStepId: stepId,
            collapsedSteps: currentCollapses
        });
        e.dataTransfer.effectAllowed = 'move';
    }

    private handlePhaseDragOver = (e: React.DragEvent, targetStepId: string) => {
        e.preventDefault();
        const { draggingStepId, steps } = this.state;
        if (!draggingStepId || draggingStepId === targetStepId) return;

        const draggingIdx = steps.findIndex(s => s.id === draggingStepId);
        const targetIdx = steps.findIndex(s => s.id === targetStepId);
        if (draggingIdx === -1 || targetIdx === -1) return;

        const updatedSteps = [...steps];
        const [removed] = updatedSteps.splice(draggingIdx, 1);
        updatedSteps.splice(targetIdx, 0, removed);

        const reindexedSteps = updatedSteps.map((s, idx) => ({
            ...s,
            stepNumber: idx + 1
        }));

        this.setState({ steps: reindexedSteps });
    }

    private handlePhaseDragEnd = () => {
        this.setState({ draggingStepId: null });
    }

    private handleGlobalFileDrop = async (e: React.DragEvent, globalFileId: string) => {
        e.preventDefault();
        e.stopPropagation();

        const files = Array.from(e.dataTransfer.files);
        if (files.length > 0) {
            const idx = this.state.globalInputs.findIndex((x: GlobalInputSlot) => x.id === globalFileId);
            if (idx === -1) return;

            const updatedInputs = [...this.state.globalInputs];
            updatedInputs[idx].meta = "Uploading...";
            this.setState({ globalInputs: updatedInputs });

            const formData = new FormData();
            formData.append('file', files[0]);
            
            const response: ApiResponse = await request({
                method: 'POST',
                url: '/ailab/upload-sandbox-file',
                data: formData,
                headers: { 'Content-Type': 'multipart/form-data' }
            });

            const finalInputs = [...this.state.globalInputs];
            if (response.isSuccess) {
                const existing = finalInputs[idx].content ? finalInputs[idx].content + "," : "";
                finalInputs[idx].content = existing + response.data.blobFileName;
                finalInputs[idx].meta = "Ready";
            } else {
                finalInputs[idx].meta = "Failed";
            }
            this.setState({ globalInputs: finalInputs });
        }
    }

    private handleFileDrop = async (e: React.DragEvent, stepId: string, slotId: string) => {
        e.preventDefault();
        e.stopPropagation();

        const files = Array.from(e.dataTransfer.files);
        if (files.length > 0) {
            const validFiles = files.filter(file => isAiDocumentFile(file.name));

            if (validFiles.length === 0) {
                alert("No supported files found. Use PDF, DOCX, HTML, TXT, CSV, JSON, XML, MD, EML, or JPG/PNG/HEIC images.");
                return;
            }

            const stepIdx = this.state.steps.findIndex((s: PipelineStep) => s.id === stepId);
            if (stepIdx === -1) return;

            const targetStep = { ...this.state.steps[stepIdx] };
            const slotIdx = targetStep.slots.findIndex(s => s.id === slotId);
            
            targetStep.slots[slotIdx].meta = `Uploading ${validFiles.length} files...`;
            const partialSteps = [...this.state.steps];
            partialSteps[stepIdx] = targetStep;
            this.setState({ steps: partialSteps });

            const uploadPromises = validFiles.map(async (file) => {
                const formData = new FormData();
                formData.append('file', file);
                const response: ApiResponse = await request({
                    method: 'POST',
                    url: '/ailab/upload-sandbox-file',
                    data: formData,
                    headers: { 'Content-Type': 'multipart/form-data' }
                });
                return response.isSuccess ? response.data.blobFileName : null;
            });

            const results = await Promise.all(uploadPromises);
            const successfulBlobs = results.filter(r => r !== null);

            const reTargetStep = { ...this.state.steps[stepIdx] };
            const existingContent = reTargetStep.slots[slotIdx].content ? reTargetStep.slots[slotIdx].content + "," : "";
            reTargetStep.slots[slotIdx].content = existingContent + successfulBlobs.join(",");
            reTargetStep.slots[slotIdx].meta = "Ready"; 

            const finalSteps = [...this.state.steps];
            finalSteps[stepIdx] = reTargetStep;
            this.setState({ steps: finalSteps });
        }
    }

    private handleCopyToClipboard = (textValue: string, tabKey: string) => {
        if (!textValue) return;

        navigator.clipboard.writeText(textValue).then(() => {
            this.setState({ copiedTab: tabKey });
            setTimeout(() => {
                this.setState({ copiedTab: null });
            }, 2000);
        }).catch(err => {
            console.error("Could not execute clipboard copy actions: ", err);
        });
    }

    private renderUniformJsonPanel = (contentValue: string, tabKey: string) => {
        const isCopied = this.state.copiedTab === tabKey;
        return (
            <div className="d-flex flex-column h-100 position-relative">
                <div className="d-flex justify-content-between align-items-center mb-3 p-2 bg-dark rounded-3 border-0 shadow-sm">
                    <div className="small fw-bold text-white text-uppercase tracking-wider px-2" style={{ fontSize: '10px', letterSpacing: '0.5px' }}>
                        Sandbox Stream Context [{tabKey.toUpperCase()}]
                    </div>
                    <Button 
                        color={isCopied ? "success" : "primary"} 
                        size="sm" 
                        className="text-white font-weight-bold text-uppercase border-0 rounded-pill shadow-sm" 
                        style={{ fontSize: '9px', letterSpacing: '0.4px', padding: '5px 14px' }}
                        onClick={() => this.handleCopyToClipboard(contentValue, tabKey)}
                    >
                        {isCopied ? '✓ Copied!' : 'Copy JSON'}
                    </Button>
                </div>
                <div className="flex-grow-1 rounded-3 border bg-dark p-4" style={{ maxHeight: '600px', overflowY: 'auto' }}>
                    <pre className="text-success font-monospace m-0 border-0" style={{ fontSize: '11px', whiteSpace: 'pre-wrap' }}>
                        {contentValue}
                    </pre>
                </div>
            </div>
        );
    }

    public render() {
        const { 
            activePreviewTab, isTesting, testOutputHtml, testOutputRawJson, testOutputLedgerJson, toolName, description, 
            steps, selectedMockClaimId, uiTemplate, showResetModal, showGuideModal,
            showPublishModal, isClaimLevel, linkedCarrierId, publishComments, configurationId, globalPersona, globalInputs, draggingStepId, collapsedSteps
        } = this.state;
        
        const { isToolPublishing, isToolUpdating, aiToolConfiguration, fileTypeSections, insCompanyDD, isInsCompanyDDLoading } = this.props;
        
        const isActionPending = isToolPublishing || isToolUpdating;
        const hasActiveDraft = aiToolConfiguration?.versions?.some((v: any) => !v.isPublished);
        const hasUnsavedVersionChanges = !!configurationId && this.state.versionContentBaseline !== undefined
            && this.getVersionContentSignature() !== this.state.versionContentBaseline;
        const canPublish = !configurationId || hasActiveDraft || hasUnsavedVersionChanges;

        let isAnySlotUploading = false;
        steps.forEach((s: PipelineStep) => {
            if (s.slots.some(sl => sl.meta?.includes("Uploading"))) {
                isAnySlotUploading = true;
            }
        });
        if (globalInputs.some(x => x.meta?.includes("Uploading"))) {
            isAnySlotUploading = true;
        }
        
        let hasDynamicSlot = globalInputs.some((gi: GlobalInputSlot) => gi.type === 'dynamic');
        if (!hasDynamicSlot) {
            steps.forEach((s: PipelineStep) => {
                if (s.slots.some((sl: ToolSlot) => sl.type === 'dynamic')) {
                    hasDynamicSlot = true;
                }
            });
        }
        const isClaimInputValid = !hasDynamicSlot || (selectedMockClaimId && selectedMockClaimId.trim() !== "");

        let badgeText = "";
        let badgeColor = "secondary";
        
        if (configurationId && aiToolConfiguration) {
            const versions: models.AiToolVersion[] = aiToolConfiguration.versions || [];
            const hasAnyPublishedVersion = versions.some(v => v.isPublished);
            const openWorkingDraft = versions.find(v => !v.isPublished);
            const highestVersionNumber = versions.length > 0 ? Math.max(...versions.map(v => v.versionNumber)) : 1;

            if (!hasAnyPublishedVersion) {
                badgeText = `NEW WORKING DRAFT (v1)`;
                badgeColor = "info";
            } else if (openWorkingDraft) {
                badgeText = `UNPUBLISHED CHANGES (v${highestVersionNumber} Draft)`;
                badgeColor = "warning";
            } else {
                const activeLiveVersion = aiToolConfiguration.latestPublishedVersion?.versionNumber || 
                                          aiToolConfiguration.currentVersion?.versionNumber || 
                                          highestVersionNumber;
                badgeText = `PUBLISHED LIVE (v${activeLiveVersion})`;
                badgeColor = "success";
            }
        }

        const hasGlobalSections = fileTypeSections && fileTypeSections.length > 0;

        return (
            <div className="py-4 bg-light" style={{ height: '100%', overflowY: 'auto' }}>
                <Container fluid className="px-4">

                    <ConfirmModal 
                        isOpen={showResetModal}
                        toggle={this.toggleResetModal}
                        onConfirm={this.handleResetConfirm}
                        title={configurationId ? "Exit Editor Pane" : "Reset Designer Canvas"}
                        message={configurationId 
                            ? "This will discard your current unsaved modifications and return to standard configuration settings."
                            : "This will wipe all current steps and templates. This action cannot be undone."}
                        confirmText={configurationId ? "Discard Changes" : "Reset Everything"}
                    />

                    <AIPublishModal 
                        isOpen={showPublishModal}
                        toggle={this.togglePublishModal}
                        configurationId={configurationId}
                        toolName={toolName}
                        description={description}
                        isClaimLevel={isClaimLevel}
                        publishComments={publishComments}
                        isActionPending={isActionPending}
                        onNameChange={(val) => this.setState({ toolName: val })}
                        onDescriptionChange={(val) => this.setState({ description: val })}
                        onClaimLevelChange={(val) => this.setState({ isClaimLevel: val })}
                        onCommentsChange={(val) => this.setState({ publishComments: val })}
                        onConfirmPublish={this.handlePublish}
                    />
                    
                    {/* Master Action Strip */}
                    <div className={`mb-4 p-4 bg-white shadow-sm rounded-3 border-bottom ${configurationId ? (hasActiveDraft ? 'border-start border-4 border-info' : 'border-start border-4 border-warning') : ''}`}>
                        <Row className="align-items-center">
                            <Col md={6}>
                                <div className="d-flex align-items-center">
                                    <div className={`${configurationId ? (hasActiveDraft ? 'bg-info' : 'bg-warning') : 'bg-primary'} p-3 rounded-3 text-white me-4 shadow-sm`}>
                                        <FaRobot size={30} />
                                    </div>
                                    <div className="flex-grow-1">
                                        <div className="d-flex align-items-center gap-2 mb-1">
                                            <Input 
                                                type="text" 
                                                value={toolName} 
                                                onChange={(e) => this.setState({toolName: e.target.value})}
                                                className="h3 fw-bold border-0 p-0 mb-0 text-dark bg-transparent no-focus-outline w-100" 
                                                placeholder="Tool Name..." 
                                            />
                                            {configurationId && (
                                                <Badge color={badgeColor} className="text-dark fw-bold px-2 py-1 ms-2">
                                                    {badgeText}
                                                </Badge>
                                            )}
                                        </div>
                                        <Input 
                                            type="text" 
                                            value={description} 
                                            onChange={(e) => this.setState({description: e.target.value})}
                                            className="small text-dark fw-bold border-0 p-0 bg-transparent w-100" 
                                            placeholder="Tool Description..." 
                                        />
                                        <div className="mt-2" style={{ maxWidth: '360px' }}>
                                            <Label className="small fw-bold text-muted text-uppercase mb-1" style={{ fontSize: '10px' }}>
                                                Linked Carrier (optional)
                                            </Label>
                                            <Input
                                                type="select"
                                                bsSize="sm"
                                                className="text-xs fw-bold"
                                                value={linkedCarrierId || 0}
                                                onChange={(e) => this.handleLinkedCarrierChange(e.target.value)}
                                                disabled={isInsCompanyDDLoading}
                                            >
                                                <option value={0}>No carrier linked — sections unresolved until claim/sandbox context</option>
                                                {insCompanyDD?.resourceList?.map((c: models.DropDownListItem) => (
                                                    <option key={c.id} value={c.id}>{c.name}</option>
                                                ))}
                                            </Input>
                                        </div>
                                    </div>
                                </div>
                            </Col>
                            <Col md={6} className="text-end">
                                <div className="d-flex justify-content-end gap-2">
                                    <Button color="info" outline className="fw-bold px-3 shadow-sm border-2 text-dark bg-white" onClick={this.toggleGuideModal}>
                                        <FaQuestionCircle className="me-2 text-primary"/> Guide
                                    </Button>

                                    <Button 
                                        color={configurationId ? "secondary" : "danger"} 
                                        outline 
                                        className="fw-bold px-3 shadow-sm border-2 bg-white" 
                                        onClick={this.toggleResetModal}
                                        disabled={isActionPending}
                                    >
                                        {configurationId ? <><FaArrowLeft className="me-2"/> Back / Cancel</> : <><FaUndo className="me-2" /> Reset</>}
                                    </Button>
                                    
                                    <Button 
                                        color="primary" 
                                        className="fw-bold px-4 shadow-sm text-white"
                                        onClick={this.handleSaveDraft}
                                        disabled={isActionPending || isAnySlotUploading}
                                    >
                                        {isToolUpdating ? 'Saving...' : 'Save Draft'}
                                    </Button>
                                    
                                    <Button 
                                        color="success" 
                                        className="fw-bold shadow-sm px-4 text-white" 
                                        onClick={this.handlePublishButtonClick}
                                        disabled={isActionPending || isAnySlotUploading || !canPublish}
                                        title={canPublish ? undefined : "No unpublished version changes. Name, description, carrier and claim-level changes go live when you save."}
                                    >
                                        <FaRocket className="me-2"/> {configurationId ? 'Publish Version' : 'Publish'}
                                    </Button>
                                </div>
                            </Col>
                        </Row>
                    </div>

                    <Row className="g-4">
                        <Col lg={5}>
                            
                            {/* GLOBAL PIPELINE ENVIRONMENT LAYER */}
                            <Card className="mb-3 p-3 bg-white shadow-sm rounded-3 border border-2 border-secondary-subtle">
                                <CardHeader className="bg-dark text-white py-2 d-flex justify-content-between align-items-center border-0">
                                    <div className="d-flex align-items-center">
                                        <FaGlobe className="me-2 text-info" size={14} />
                                        <span className="small fw-bold text-uppercase tracking-wider" style={{ letterSpacing: '0.5px', fontSize: '11px' }}>Global Pipeline Environment Layer</span>
                                    </div>
                                </CardHeader>
                                <CardBody className="bg-white pt-3 px-3">
                                    <div className="mb-3">
                                        <Label className="small fw-bold text-muted text-uppercase mb-1" style={{ fontSize: '10px' }}>Global System Identity Persona / Directives</Label>
                                        <Input 
                                            type="textarea" 
                                            rows={2} 
                                            className="font-monospace text-xs bg-light border-0 text-dark fw-bold" 
                                            value={globalPersona}
                                            onChange={(e) => this.setState({ globalPersona: e.target.value })}
                                            style={{ borderRadius: '6px', fontSize: '12px', resize: 'none' }}
                                        />
                                    </div>

                                    {/* Global Environmental Inputs */}
                                    {globalInputs.map((giBlock: GlobalInputSlot) => (
                                        <Card key={giBlock.id} className="mb-3 p-3 bg-white border border-2 border-secondary-subtle rounded-3" style={{ boxShadow: 'none' }}>
                                            <CardHeader className="bg-transparent border-0 p-0 pb-2 mb-2 d-flex justify-content-between align-items-center border-bottom">
                                                <span className="small fw-bold text-muted" style={{ fontSize: '11px' }}>
                                                    {giBlock.type === 'dynamic' ? <><FaDatabase className="text-primary me-2"/>Claim JSON Injection</> : <><FaFileAlt className="me-2 text-info"/>Master Rules Catalog / File Bundle</>}
                                                </span>
                                                <FaTrash className="text-muted cursor-pointer" size={10} onClick={() => this.removeGlobalInputSlot(giBlock.id)} style={{ cursor: 'pointer' }} />
                                            </CardHeader>
                                            <CardBody className="p-0">
                                                {giBlock.type === 'dynamic' ? (
                                                    <div>
                                                        <Input 
                                                            type="select"
                                                            bsSize="sm"
                                                            className="text-xs fw-bold border-1 mb-1"
                                                            value={giBlock.alias}
                                                            onChange={(e) => this.updateGlobalInputSlotProperty(giBlock.id, 'alias', e.target.value)}
                                                        >
                                                            <option value="{{Claim_JSON}}">Target Context Model: Claim JSON Document</option>
                                                            <option value="{{Field_Adjuster_JSON}}">Target Context Model: Field Adjuster Metrics JSON</option>
                                                            <option value="{{Carrier_Company_JSON}}">Target Context Model: Carrier Company Volume Metrics</option>
                                                        </Input>
                                                        <div className="p-1 bg-primary bg-opacity-10 border border-primary border-opacity-20 rounded-2 text-center text-primary font-monospace fw-bold" style={{ fontSize: '10px' }}>
                                                            Dynamic Variable Route Set: {giBlock.alias}
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <div>
                                                        <Row className="mb-2 g-2">
                                                            <Col xs={6}>
                                                                <Label className="text-uppercase text-muted fw-bold mb-0" style={{ fontSize: '9px' }}><FaTags className="me-1"/> Token Keyword Alias</Label>
                                                                <Input 
                                                                    type="text" 
                                                                    bsSize="sm"
                                                                    placeholder="e.g., #CarrierRules"
                                                                    className="font-monospace text-xs fw-bold border-1"
                                                                    value={giBlock.alias}
                                                                    onChange={(e) => this.updateGlobalInputSlotProperty(giBlock.id, 'alias', e.target.value)}
                                                                />
                                                            </Col>
                                                            <Col xs={6}>
                                                                <Label className="text-uppercase text-muted fw-bold mb-0" style={{ fontSize: '9px' }}><FaFileAlt className="me-1"/> System File Type Map</Label>
                                                                <Input 
                                                                    type="select" 
                                                                    bsSize="sm"
                                                                    className="text-xs fw-bold border-1"
                                                                    value={giBlock.companyFileTypeId || 0}
                                                                    onChange={(e) => this.updateGlobalInputSlotProperty(giBlock.id, 'companyFileTypeId', parseInt(e.target.value, 10))}
                                                                >
                                                                    <option value={0}>Manual Upload (Sandbox Only)</option>
                                                                    {this.props.fileTypesDD?.resourceList?.map((t: any) => (
                                                                        <option key={t.id} value={t.id}>Linked Catalog: {t.name}</option>
                                                                    ))}
                                                                </Input>
                                                            </Col>
                                                        </Row>

                                                        {(!giBlock.companyFileTypeId || giBlock.companyFileTypeId === 0) ? (
                                                            <div 
                                                                className="p-2 border-2 border-dashed rounded-3 text-center bg-white cursor-pointer"
                                                                onDrop={(e) => this.handleGlobalFileDrop(e, giBlock.id)}
                                                                onDragOver={this.handleDragOver}
                                                            >
                                                                <FaCloudUploadAlt size={16} className="text-primary me-2" />
                                                                <span className="small text-muted" style={{ fontSize: '11px' }}>
                                                                    {giBlock.content ? `${giBlock.content.split(',').length} Files Staged` : "Drop Staged Sandbox Reference File"}
                                                                </span>
                                                            </div>
                                                        ) : (
                                                            <div className="p-1 bg-info bg-opacity-10 border border-info rounded-2 text-center text-info" style={{ fontSize: '11px' }}>
                                                                <FaDatabase className="me-1" /> Bound to Integrated Corporate Storage System ({this.props.fileTypeSections ? this.props.fileTypeSections.length : 0} Sections Parsed)
                                                                {!linkedCarrierId && (
                                                                    <div className="text-warning fw-bold mt-1" style={{ fontSize: '10px' }}>
                                                                        Link a carrier above to load carrier-specific section headers for the designer.
                                                                    </div>
                                                                )}
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                            </CardBody>
                                        </Card>
                                    ))}

                                    <div className="d-flex justify-content-end mt-2">
                                        <UncontrolledDropdown>
                                            <DropdownToggle color="secondary" size="sm" className="fw-bold rounded-pill text-xs px-3 text-white shadow-sm">
                                                <FaPlus className="me-1 text-white"/> Add Input
                                            </DropdownToggle>
                                            <DropdownMenu right className="shadow border-0">
                                                <DropdownItem onClick={() => this.addGlobalInputSlot('dynamic')}><FaDatabase className="me-2 text-primary"/> Claim JSON Injection</DropdownItem>
                                                <DropdownItem onClick={() => this.addGlobalInputSlot('file')}><FaFileAlt className="me-2 text-info"/> Master Rules Catalog</DropdownItem>
                                            </DropdownMenu>
                                        </UncontrolledDropdown>
                                    </div>
                                </CardBody>
                            </Card>

                            <hr className="my-3 border-secondary-subtle" />

                            <div className="d-flex justify-content-between align-items-center mb-3 px-1">
                                <h6 className="small fw-bold text-uppercase text-dark mb-0">Sequential Graph Steps</h6>
                            </div>

                            <div className="d-flex justify-content-between align-items-center mb-3 px-3 py-2 bg-dark rounded-3 border-0 shadow-sm">
                                <div className="small fw-bold text-white text-uppercase tracking-wider" style={{ fontSize: '10px', letterSpacing: '0.5px' }}>
                                    Graph Sequence Scope ({steps.length} Phases Active)
                                </div>
                                <div className="d-flex gap-2">
                                    <Button 
                                        color="primary" 
                                        size="sm" 
                                        className="text-white font-weight-bold text-uppercase border-0 rounded-pill shadow-sm" 
                                        style={{ fontSize: '9px', letterSpacing: '0.4px', padding: '4px 14px' }}
                                        onClick={() => this.toggleBulkPhaseDisplay(false)}
                                    >
                                        Expand All
                                    </Button>
                                    <Button 
                                        color="secondary" 
                                        size="sm" 
                                        className="text-white font-weight-bold text-uppercase border-0 rounded-pill shadow-sm" 
                                        style={{ fontSize: '9px', letterSpacing: '0.4px', padding: '4px 14px' }}
                                        onClick={() => this.toggleBulkPhaseDisplay(true)}
                                    >
                                        Collapse All
                                    </Button>
                                </div>
                            </div>

                            {/* Sequential Step Process Pipeline */}
                            {steps.map((step: PipelineStep) => {
                                const isCollapsed = collapsedSteps[step.id] ?? true; 
                                const stepSelectedKeys = step.selectedSectionKeys || [];

                                return (
                                    <div 
                                        key={step.id} 
                                        className={`mb-3 bg-white shadow-sm rounded-3 border border-2 border-secondary-subtle transition-all ${draggingStepId === step.id ? 'opacity-50 border-primary' : ''}`}
                                        draggable
                                        onDragStart={(e) => this.handlePhaseDragStart(e, step.id)}
                                        onDragOver={(e) => this.handlePhaseDragOver(e, step.id)}
                                        onDragEnd={this.handlePhaseDragEnd}
                                    >
                                        <div 
                                            className="d-flex justify-content-between align-items-center p-3 cursor-pointer user-select-none border-bottom-0"
                                            onClick={() => this.toggleStepCollapse(step.id)}
                                            style={{ cursor: 'pointer' }}
                                        >
                                            <div className="d-flex align-items-center gap-2" onClick={(e) => e.stopPropagation()}>
                                                <div 
                                                    className="text-muted p-1 cursor-grab active:cursor-grabbing me-1"
                                                    draggable
                                                    onDragStart={(e) => this.handlePhaseDragStart(e, step.id)}
                                                >
                                                    <FaArrowsAltV size={13} />
                                                </div>
                                                <Badge color="dark" className="rounded-circle px-2 py-1">{step.stepNumber}</Badge>
                                                <Input 
                                                    type="text" 
                                                    bsSize="sm"
                                                    className="fw-bold border-0 bg-transparent text-dark p-0 font-weight-bold no-focus-outline" 
                                                    value={step.title}
                                                    style={{ fontSize: '14px', width: '220px' }}
                                                    onChange={(e) => this.updateStepProperty(step.id, 'title', e.target.value)}
                                                />
                                            </div>
                                            <div className="d-flex align-items-center gap-3">
                                                {step.isOptionalPhase && <Badge color="warning" className="text-dark fw-bold text-uppercase" style={{ fontSize: '9px' }}>Optional</Badge>}
                                                <FaTrash 
                                                    className="text-danger cursor-pointer" 
                                                    onClick={(e) => {
                                                        e.stopPropagation(); 
                                                        this.removeStepGroup(step.id);
                                                    }} 
                                                    style={{ cursor: 'pointer' }} 
                                                    size={12} 
                                                />
                                                <span className="text-muted me-1">
                                                    {isCollapsed ? <FaChevronDown size={12} /> : <FaChevronUp size={12} />}
                                                </span>
                                            </div>
                                        </div>

                                        <Collapse isOpen={!isCollapsed}>
                                            <div className="p-3 pt-0 border-top border-light">
                                                <Row className="mb-3 g-2">
                                                    <Col xs={6}>
                                                        <Label className="text-uppercase text-muted fw-bold mb-0" style={{ fontSize: '9px' }}><FaMicrochip className="me-1"/> Execution Brain</Label>
                                                        <Input 
                                                            type="select" 
                                                            bsSize="sm" 
                                                            className="fw-bold text-xs"
                                                            value={step.selectedModel}
                                                            onChange={(e) => this.updateStepProperty(step.id, 'selectedModel', e.target.value)}
                                                        >
                                                            {AVAILABLE_MODELS.map(m => (
                                                                <option key={m.id} value={m.id}>{m.name}</option>
                                                            ))}
                                                        </Input>
                                                    </Col>
                                                    <Col xs={6}>
                                                        <Label className="text-uppercase text-muted fw-bold mb-0" style={{ fontSize: '9px' }}>Phase Gate Requirement</Label>
                                                        <div className="d-flex align-items-center mt-1">
                                                            <Input 
                                                                type="checkbox" 
                                                                id={`optional-switch-${step.id}`}
                                                                className="me-2 cursor-pointer ms-1"
                                                                checked={step.isOptionalPhase}
                                                                onChange={(e) => this.updateStepProperty(step.id, 'isOptionalPhase', e.target.checked)}
                                                            />
                                                            <Label for={`optional-switch-${step.id}`} className="small mb-0 text-muted fw-bold cursor-pointer text-uppercase" style={{ fontSize: '10px' }}>
                                                                {step.isOptionalPhase ? <span className="text-warning">Optional Phase Bypass</span> : <span className="text-dark">★ Mandatory Evaluation</span>}
                                                            </Label>
                                                        </div>
                                                    </Col>
                                                </Row>

                                                {/* PHASE-LEVEL SECTION PICKER */}
                                                {hasGlobalSections && (
                                                    <div className="p-2 mb-3 bg-light border rounded-3">
                                                        <div className="d-flex justify-content-between align-items-center mb-1 pb-1 border-bottom">
                                                            <Label className="small mb-0 text-dark fw-bold text-uppercase d-flex align-items-center" style={{ fontSize: '10px' }}>
                                                                <FaListUl className="me-1 text-primary" /> Global File Sections ({stepSelectedKeys.length}/{fileTypeSections.length})
                                                            </Label>
                                                            <div className="d-flex gap-1">
                                                                <Button 
                                                                    color="link" 
                                                                    size="sm" 
                                                                    className="p-0 text-primary text-decoration-none" 
                                                                    style={{ fontSize: '9px' }}
                                                                    onClick={() => this.toggleAllStepSections(step.id, fileTypeSections.map((s: models.DocumentSectionMeta) => s.key), true)}
                                                                >
                                                                    All
                                                                </Button>
                                                                <span className="text-muted" style={{ fontSize: '9px' }}>|</span>
                                                                <Button 
                                                                    color="link" 
                                                                    size="sm" 
                                                                    className="p-0 text-muted text-decoration-none" 
                                                                    style={{ fontSize: '9px' }}
                                                                    onClick={() => this.toggleAllStepSections(step.id, [], false)}
                                                                >
                                                                    Clear
                                                                </Button>
                                                            </div>
                                                        </div>
                                                        <div style={{ maxHeight: '150px', overflowY: 'auto' }} className="pe-1">
                                                            {fileTypeSections.map((sec: models.DocumentSectionMeta) => {
                                                                const isChecked = stepSelectedKeys.includes(sec.key);
                                                                return (
                                                                    <div 
                                                                        key={sec.key} 
                                                                        className={`d-flex align-items-center p-1 rounded-1 mb-1 cursor-pointer ${isChecked ? 'bg-primary bg-opacity-10' : 'bg-white'}`}
                                                                        onClick={() => this.toggleStepSectionKey(step.id, sec.key)}
                                                                        style={{ cursor: 'pointer' }}
                                                                    >
                                                                        <div className="me-2 text-primary">
                                                                            {isChecked ? <FaCheckSquare size={12} /> : <FaSquare className="text-muted opacity-50" size={12} />}
                                                                        </div>
                                                                        <span className="small text-truncate" style={{ fontSize: '11px', color: isChecked ? '#0d6efd' : '#495057' }}>
                                                                            {sec.title}
                                                                        </span>
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    </div>
                                                )}

                                                {/* Phase Document Ingestion Slots */}
                                                {step.slots.map((slot: ToolSlot) => (
                                                    <Card key={slot.id} className="shadow-none border border-1 bg-light mb-2 overflow-hidden" style={{ borderRadius: '8px' }}>
                                                        <CardHeader className="bg-transparent border-0 py-1 d-flex justify-content-between align-items-center border-bottom">
                                                            <span className="small fw-bold text-muted" style={{ fontSize: '11px' }}>{slot.title}</span>
                                                            <div className="d-flex align-items-center">
                                                                {slot.meta && <Badge color="primary" className="text-uppercase fw-bold me-2" style={{ fontSize: '8px' }}>{slot.meta}</Badge>}
                                                                <FaTrash className="text-muted cursor-pointer" size={10} onClick={() => this.removeSlotFromStep(step.id, slot.id)} style={{ cursor: 'pointer' }} />
                                                            </div>
                                                        </CardHeader>
                                                        <CardBody className="p-2">
                                                            {slot.type === 'file' && (
                                                                <div>
                                                                    <div className="mb-2">
                                                                        <Label className="text-uppercase text-muted fw-bold mb-0" style={{ fontSize: '9px' }}>
                                                                            Slot Context Name / Alias
                                                                        </Label>
                                                                        <Input 
                                                                            type="text" 
                                                                            bsSize="sm"
                                                                            placeholder="e.g., #PolicyDoc, #PhotoSheet"
                                                                            className="font-monospace text-xs fw-bold border-1 mb-1"
                                                                            value={slot.label || ''}
                                                                            onChange={(e) => {
                                                                                const val = e.target.value;
                                                                                const updatedSlots = step.slots.map(sl => sl.id === slot.id ? { ...sl, label: val } : sl);
                                                                                this.updateStepProperty(step.id, 'slots', updatedSlots);
                                                                            }}
                                                                        />
                                                                    </div>

                                                                    <Input 
                                                                        type="select" 
                                                                        bsSize="sm"
                                                                        className="form-control-sm mb-2 text-xs"
                                                                        value={slot.companyFileTypeId || 0}
                                                                        onChange={(e) => {
                                                                            const val = parseInt(e.target.value, 10);
                                                                            const updatedSlots = step.slots.map(sl => {
                                                                                if (sl.id === slot.id) {
                                                                                    return { 
                                                                                        ...sl, 
                                                                                        companyFileTypeId: val, 
                                                                                        content: val > 0 ? "" : sl.content
                                                                                    };
                                                                                }
                                                                                return sl;
                                                                            });
                                                                            this.updateStepProperty(step.id, 'slots', updatedSlots);
                                                                        }}
                                                                    >
                                                                        <option value={0}>Manual Upload (Sandbox Only)</option>
                                                                        {this.props.fileTypesDD?.resourceList?.map((t: any) => (
                                                                            <option key={t.id} value={t.id}>Catalog Reference: {t.name}</option>
                                                                        ))}
                                                                    </Input>
                                                                    
                                                                    {(!slot.companyFileTypeId || slot.companyFileTypeId === 0) ? (
                                                                        <div 
                                                                            className="p-2 border-2 border-dashed rounded-3 text-center bg-white" 
                                                                            onDrop={(e) => this.handleFileDrop(e, step.id, slot.id)}
                                                                            onDragOver={this.handleDragOver}
                                                                        >
                                                                            <FaCloudUploadAlt size={16} className="text-primary me-2" />
                                                                            <span className="small text-muted" style={{ fontSize: '11px' }}>
                                                                                {slot.content ? `${slot.content.split(',').length} Staged` : "Drop Staged Sandbox File(s)"}
                                                                            </span>
                                                                        </div>
                                                                    ) : (
                                                                        <div className="p-1 bg-info bg-opacity-10 border border-info rounded-2 text-center text-info" style={{ fontSize: '11px' }}>
                                                                            <FaDatabase className="me-1" /> Linked Document Reference
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            )}

                                                            {slot.type !== 'file' && (
                                                                <Input 
                                                                    type="textarea" 
                                                                    rows={2} 
                                                                    className="font-monospace text-xs bg-white border-1 text-dark" 
                                                                    value={slot.content}
                                                                    onChange={(e) => {
                                                                        const updatedSlots = step.slots.map(sl => sl.id === slot.id ? { ...sl, content: e.target.value } : sl);
                                                                        this.updateStepProperty(step.id, 'slots', updatedSlots);
                                                                    }}
                                                                    disabled={slot.type === 'dynamic'}
                                                                    style={{ borderRadius: '4px', fontSize: '11px', resize: 'none' }}
                                                                />
                                                            )}
                                                        </CardBody>
                                                    </Card>
                                                ))}

                                                <div className="d-flex justify-content-end mt-2">
                                                    <UncontrolledDropdown>
                                                        <DropdownToggle color="secondary" size="sm" className="fw-bold rounded-pill text-xs px-3 text-white shadow-sm">
                                                            <FaPlus className="me-1 text-white"/> Add Input
                                                        </DropdownToggle>
                                                        <DropdownMenu right className="shadow border-0">
                                                            <DropdownItem onClick={() => this.addSlotToStep(step.id, 'dynamic')}><FaDatabase className="me-2 text-primary"/> Claim JSON Injection</DropdownItem>
                                                            <DropdownItem onClick={() => this.addSlotToStep(step.id, 'file')}><FaFileAlt className="me-2 text-info"/> Generic File Bundle</DropdownItem>
                                                            <DropdownItem onClick={() => this.addSlotToStep(step.id, 'static')}><FaRobot className="me-2 text-success"/> Static Instruction</DropdownItem>
                                                        </DropdownMenu>
                                                    </UncontrolledDropdown>
                                                </div>
                                            </div>
                                        </Collapse>
                                    </div>
                                );
                            })}

                            <div className="text-center mt-3 mb-4">
                                <Button color="primary" size="sm" className="py-2 px-5 fw-bold rounded-pill shadow-sm w-100" onClick={this.addNewStepGroup}>
                                    <FaLayerGroup className="me-2"/> Add Sequential Phase
                                </Button>
                            </div>

                            <Card className="shadow-sm border-0 mt-4" style={{ borderRadius: '15px', borderLeft: '5px solid #198754' }}>
                                <CardHeader className="bg-white border-0 py-3 border-bottom">
                                    <h6 className="mb-0 fw-bold text-success"><FaCode className="me-2" /> UI Presentation Template</h6>
                                </CardHeader>
                                <CardBody>
                                    <Input 
                                        type="textarea" 
                                        rows={4} 
                                        className="font-monospace text-xs bg-dark text-success border-0 p-3" 
                                        value={uiTemplate}
                                        onChange={(e) => this.setState({ uiTemplate: e.target.value })}
                                        placeholder="Enter HTML template..."
                                        style={{ borderRadius: '10px', fontSize: '12px', resize: 'none' }}
                                    />
                                </CardBody>
                            </Card>
                        </Col>

                        {/* Sandbox Simulation Panel */}
                        <Col lg={7}>
                            <Card className="shadow-sm border-0 h-100 overflow-hidden position-relative" style={{ borderRadius: '15px' }}>
                                <CardHeader className="bg-dark text-white border-0 d-flex justify-content-between align-items-center py-3">
                                    <div className="d-flex align-items-center gap-2">
                                        <FaPlay className="me-1 text-info" />
                                        <span className="fw-bold text-uppercase small me-2" style={{letterSpacing: '1px'}}>Sandbox</span>
                                        
                                        {hasDynamicSlot && (
                                            <div className="d-flex align-items-center ms-3">
                                                <Label className="small fw-bold text-uppercase mb-0 me-2 text-white" style={{ fontSize: '11px', letterSpacing: '0.5px', whiteSpace: 'nowrap' }}>
                                                    Target Context Identifier:
                                                </Label>
                                                <Input 
                                                    type="text"
                                                    bsSize="sm" 
                                                    className="bg-white text-dark border-0 fw-bold font-monospace px-3" 
                                                    placeholder="Enter Target Context Lookup Variable..."
                                                    style={{ width: '240px', fontSize: '12px', borderRadius: '6px' }}
                                                    value={selectedMockClaimId || ""} 
                                                    onChange={(e) => this.setState({ selectedMockClaimId: e.target.value })}
                                                />
                                            </div>
                                        )}
                                    </div>
                                    <Button 
                                        color="info" size="sm" 
                                        className="text-white fw-bold px-4 shadow-sm" 
                                        onClick={this.handleRunTest} 
                                        disabled={isTesting || isAnySlotUploading || !isClaimInputValid}
                                    >
                                        {isTesting ? 'Executing (Queued)...' : isAnySlotUploading ? 'Uploading...' : 'Run Test Draft'}
                                    </Button>
                                </CardHeader>
                                <CardBody className="p-0 d-flex flex-column bg-white position-relative">
                                    <Nav tabs className="px-3 bg-light border-0">
                                        <NavItem>
                                            <NavLink className={`py-3 px-4 border-0 ${activePreviewTab === 'preview' ? 'active border-bottom border-primary text-primary fw-bold' : 'text-dark fw-bold opacity-50'}`} onClick={() => this.togglePreviewTab('preview')} style={{cursor: 'pointer', backgroundColor: 'transparent'}}>
                                                <FaEye className="me-2" /> Adjuster Preview
                                            </NavLink>
                                        </NavItem>
                                        <NavItem>
                                            <NavLink className={`py-3 px-4 border-0 ${activePreviewTab === 'json' ? 'active border-bottom border-primary text-primary fw-bold' : 'text-dark fw-bold opacity-50'}`} onClick={() => this.togglePreviewTab('json')} style={{cursor: 'pointer', backgroundColor: 'transparent'}}>
                                                <FaFileAlt className="me-2" /> Raw JSON Response
                                            </NavLink>
                                        </NavItem>
                                        <NavItem>
                                            <NavLink className={`py-3 px-4 border-0 ${activePreviewTab === 'ledger' ? 'active border-bottom border-primary text-primary fw-bold' : 'text-dark fw-bold opacity-50'}`} onClick={() => this.togglePreviewTab('ledger')} style={{cursor: 'pointer', backgroundColor: 'transparent'}}>
                                                <FaDatabase className="me-2" /> Internal Ledger
                                            </NavLink>
                                        </NavItem>
                                        <NavItem>
                                            <NavLink className={`py-3 px-4 border-0 ${activePreviewTab === 'code' ? 'active border-bottom border-primary text-primary fw-bold' : 'text-dark fw-bold opacity-50'}`} onClick={() => this.togglePreviewTab('code')} style={{cursor: 'pointer', backgroundColor: 'transparent'}}>
                                                <FaCode className="me-2" /> Unified Context
                                            </NavLink>
                                        </NavItem>
                                    </Nav>
                                    <TabContent activeTab={activePreviewTab} className="flex-grow-1 p-4 bg-light" style={{ minHeight: '600px' }}>
                                        <TabPane tabId="preview">
                                        {isTesting ? (
                                            <div className="d-flex flex-column align-items-center justify-content-center py-5 mt-5 text-center">
                                                <div className="mb-4">   
                                                    <Spinner color="primary" style={{ width: '4rem', height: '4rem' }}>
                                                        <span className="visually-hidden">Loading...</span>
                                                    </Spinner>
                                                </div>
                                                <div>
                                                    <h4 className="fw-bold text-primary mb-2">Executing Background Pipeline</h4>
                                                    <p className="small text-muted fw-bold mb-0">
                                                        Job queued on background execution runner. Polling worker status...<br />
                                                        Processing sequential canvas nodes and assembling ledger state.
                                                    </p>
                                                </div>
                                            </div>
                                        ) : testOutputHtml ? (
                                            <div>
                                                <div dangerouslySetInnerHTML={{ __html: testOutputHtml }} />
                                            </div>
                                        ) : (
                                            <div className="d-flex flex-column align-items-center justify-content-center py-5 text-dark opacity-50 mt-5">
                                                <FaRobot size={60} className="mb-3" />
                                                <h5 className="fw-bold">Sandbox Ready</h5>
                                                <p className="fw-bold text-center">Set your pipeline parameters above<br/>and execute a sandbox run simulation.</p>
                                            </div>
                                        )}
                                        </TabPane>
                                        
                                        <TabPane tabId="json" className="h-100">
                                            {testOutputRawJson ? (
                                                this.renderUniformJsonPanel(testOutputRawJson, 'json')
                                            ) : (
                                                <div className="d-flex flex-column align-items-center justify-content-center py-5 text-dark opacity-50 mt-5">
                                                    <FaFileAlt size={60} className="mb-3" />
                                                    <h5 className="fw-bold">No JSON Data</h5>
                                                    <p className="fw-bold text-center">Execute a test run scenario to examine<br/>the payload string emitted by the AI agent.</p>
                                                </div>
                                            )}
                                        </TabPane>

                                        <TabPane tabId="ledger" className="h-100">
                                            {testOutputLedgerJson ? (
                                                this.renderUniformJsonPanel(testOutputLedgerJson, 'ledger')
                                            ) : (
                                                <div className="d-flex flex-column align-items-center justify-content-center py-5 text-dark opacity-50 mt-5">
                                                    <FaDatabase size={60} className="mb-3" />
                                                    <h5 className="fw-bold">No Ledger Context</h5>
                                                    <p className="fw-bold text-center">Execute a test run scenario to map<br/>the sequence process tracing ledger arrays.</p>
                                                </div>
                                            )}
                                        </TabPane>

                                        <TabPane tabId="code" className="h-100">
                                            {this.renderUniformJsonPanel(
                                                JSON.stringify({ tool: toolName, globalPersona: globalPersona, globalInputs: globalInputs, steps: steps, template: uiTemplate }, null, 2), 
                                                'code'
                                            )}
                                        </TabPane>
                                    </TabContent>
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                </Container>
                
                <AIDesignerGuideModal 
                    isOpen={showGuideModal} 
                    toggle={this.toggleGuideModal} 
                />
            </div>                    
        );
    }
}

const mapStateToProps = (state: MyTypes.RootState) => ({
    aiToolConfiguration: state.aiLab.aiLab.aiToolConfiguration,
    fileTypesDD: state.companies.dropdowns.companyFileTypesDD,
    fileTypeSections: state.companies.companyFileTypes.companyFileTypeSections,
    insCompanyDD: state.companies.dropdowns.insCompanyDD,
    isInsCompanyDDLoading: state.companies.dropdowns.isInsCompanyDDLoading,
    isToolPublishing: state.aiLab.aiLab.isPublishingTool, 
    publishError: state.aiLab.aiLab.publishToolError,
    isToolUpdating: state.aiLab.aiLab.isAiToolConfigurationUpdating,
    updateError: state.aiLab.aiLab.updateError
});

const mapDispatchToProps = (dispatch: Dispatch) =>
    bindActionCreators({
        requestCompanyFileTypesDD: companyActions.requestCompanyFileTypesDD,
        requestInsCompanyDD: companyActions.requestInsCompanyDD,
        requestFileTypeSections: companyActions.requestFileTypeSections,
        requestAiToolConfiguration: aiLabActions.requestAiToolConfiguration,
        publishAiToolVersion: aiLabActions.requestAIToolPublish, 
        updateAiToolConfiguration: aiLabActions.updateAiToolConfiguration,
        createAiToolConfiguration: aiLabActions.createAiToolConfiguration
    },
    dispatch
);

const connector = connect(mapStateToProps, mapDispatchToProps); 
type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(AIDesignerContainer);